import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Dropdown,
  Icon,
  Modal,
  Text,
} from '../../../deprecated-components/BaseComponents'
import { MarkInactiveDialog } from './MarkInactiveDialog'
import {
  Header,
  Button as SemanticButton,
  Card as SemanticCard,
  Label,
  Grid,
} from 'semantic-ui-react'
import { DateTime } from 'luxon'

import { deleteFinancialAccount } from '../../../actions/financialAccountActions'
import {
  updateAdminUserFinancialAccounts,
  getDateOfLastTransaction,
  forcePullTransactions,
  forceNotifyUserReconnection,
  adminDeleteFinancialAccount,
  fetchUserFinancialAccounts,
  adminLinkFinancialAccount,
  ADMIN_LINK_FINANCIAL_ACCOUNT_KEY,
} from '../../../actions/admin/adminFinancialAccountActions'
import { Alert } from '@/ui'
import { getFetchError } from '@/reducers/fetch'
import { PlaidLink } from '@/components/plaid/PlaidLink'
import { FinancialAccount } from '../../../reducers/finances/financialAccountsReducer'
import { FinancialAccountWithAdminInfo } from '../../../reducers/admin/financialAccountsReducer'
import { DATE_FORMATS_LUXON } from '../../../utils/dateHelpers'
import { useReselector } from '../../../utils/sharedHooks'
import { getPlaidOnlyFinancialAccountsByUserId } from '../../../selectors/financeSelectors'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { useAppDispatch } from '../../../utils/typeHelpers'

const ConfirmExampleConfirm = ({
  isAdmin,
  account,
  userId,
}: {
  isAdmin: boolean
  account: FinancialAccount | FinancialAccountWithAdminInfo
  userId: number
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const removeAccount = useCallback(async () => {
    if (isAdmin) {
      await dispatch(adminDeleteFinancialAccount(userId, account.id))
    } else {
      await dispatch(deleteFinancialAccount(account.id))
    }
    setOpen(false)
  }, [account.id, dispatch, isAdmin, userId])

  return (
    <div>
      <Button
        variant="link"
        onClick={() => setOpen(true)}
        floated="right"
        icon="trash alternate outline"
      />

      <Modal
        size="tiny"
        dimmer="inverted"
        className="twoFactorAuthenticationModal"
        open={open}
        onClose={() => setOpen(false)}
        closeIcon
      >
        <Modal.Content>
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <Text as="bodyMd">
                  Are you sure you want to remove ${account.name}: $
                  {account.mask} from your account? This will remove all
                  transactions associated with it.
                </Text>
                {account.accountType === 'plaid' && (
                  <Text as="bodySm" className="mt-2">
                    Note: this account is still shared at the institution and
                    may reappear on the next sync. To keep it gone, hide it
                    instead, or have the user deselect it in Plaid.
                  </Text>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={removeAccount}>
            Remove
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

const LinkManualAccount = ({
  account,
  userId,
}: {
  account: FinancialAccount | FinancialAccountWithAdminInfo
  userId: number
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [plaidAccountId, setPlaidAccountId] = useState<number>()

  const plaidAccounts = useReselector(
    getPlaidOnlyFinancialAccountsByUserId,
    userId
  )

  const error = useReselector(getFetchError, ADMIN_LINK_FINANCIAL_ACCOUNT_KEY)

  const plaidAccountOptions = plaidAccounts.map((account) => ({
    text: `${account.plaidInstitutionName} ${account.name}: ${account.mask}`,
    value: account.id,
  }))

  useEffect(() => {
    dispatch(fetchUserFinancialAccounts(userId))
  }, [dispatch, userId])

  const linkAccount = async () => {
    if (!plaidAccountId) {
      setOpen(false)
      return
    }
    const data = { userId, financialAccountId: account.id, plaidAccountId }
    const linked = await dispatch(
      adminLinkFinancialAccount(userId, account.id, data)
    )
    // A refused link names the reconciliation periods that collided; closing here would
    // throw that away and read as the click doing nothing.
    if (linked) {
      setOpen(false)
    }
  }

  return (
    <div>
      <Button
        variant="link"
        onClick={() => setOpen(true)}
        floated="right"
        icon="linkify"
      />

      <Modal
        size="tiny"
        dimmer="inverted"
        className="twoFactorAuthenticationModal"
        open={open}
        onClose={() => setOpen(false)}
        closeIcon
      >
        <Modal.Content>
          <Dropdown
            fluid
            label="Plaid Connected Account"
            placeholder="Select Account"
            options={plaidAccountOptions}
            onChange={setPlaidAccountId}
            value={plaidAccountId}
          />
          {error && <Alert variant="error">{error.message}</Alert>}
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={linkAccount}>
            Link
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  )
}

const AccountCard = ({
  account,
  isAdmin,
  userId,
}: {
  account: FinancialAccount | FinancialAccountWithAdminInfo
  isAdmin?: boolean
  userId: number
}) => {
  const dispatch = useAppDispatch()
  const [submitting, setSubmitting] = useState(false)
  const [notifying, setNotifying] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false)
  const [dateOfLastTransaction, setDateOfLastTransaction] = useState<string>()

  useEffect(() => {
    const fetchData = async () => {
      const res = await getDateOfLastTransaction(account.id)(dispatch)
      if (res) {
        setDateOfLastTransaction(
          DateTime.fromISO(res.date, { zone: 'utc' }).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_SHORT
          )
        )
      }
    }

    fetchData()
  }, [account, dispatch])

  const pullTransactions = async () => {
    setSubmitting(true)
    const data = {
      accountId: account.id,
    }
    const res = await forcePullTransactions(data)(dispatch)
    if (!res) {
      setSubmitting(false)
      setStatus('Cannot force pull')
    }
    setSubmitting(false)
    setStatus('Force pulled')
  }

  const notifyUser = async () => {
    setNotifying(true)
    const data = {
      accountId: account.id,
    }

    const res = await forceNotifyUserReconnection(data)(dispatch)
    if (typeof res !== 'boolean') {
      setStatus(res.status)
    }

    setNotifying(false)
  }

  const handleMarkInactive = () => setInactiveModalOpen(true)

  const markAccountInactive = async () => {
    const res = await updateAdminUserFinancialAccounts(userId, account.id, {
      inactive: true,
    })(dispatch)

    setStatus(res ? 'Set Inactive' : 'Cannot update as inactive')
  }

  const forcePullDisabled = () => {
    return (
      account.accountType !== 'plaid' ||
      account?.accounts?.needsReconnection ||
      account.inactive
    )
  }

  const notifyDisabled = () => {
    return (
      account.accountType !== 'plaid' ||
      !account?.accounts?.needsReconnection ||
      account.inactive
    )
  }

  const noRecentPlaidTransactions =
    Math.abs(
      DateTime.now().diff(
        DateTime.fromFormat(
          dateOfLastTransaction || '',
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        ),
        'days'
      ).days
    ) > 7 && account.accountType === 'plaid'

  let className = 'accountCard'

  if (account?.accounts?.needsReconnection) {
    className = `${className} needsReconnection`
  }

  if (isAdmin) {
    if (noRecentPlaidTransactions) {
      className = `${className} noRecentTransactions`
    }

    return (
      <SemanticCard className={className} fluid>
        <SemanticCard.Content>
          <div className="float-right flex items-center gap-1">
            {isAdmin && account.accountType === 'manual' && (
              <LinkManualAccount account={account} userId={userId} />
            )}
            <ConfirmExampleConfirm
              account={account}
              isAdmin={isAdmin}
              userId={userId}
            />
          </div>
          <Label
            color={account.accountType === 'plaid' ? 'green' : 'red'}
            horizontal
          >
            {account.accountType}
          </Label>

          {account.inactive && (
            <Label color="orange" horizontal>
              Inactive
            </Label>
          )}

          {/* Show if inactive and plaid connected since only plaid accounts can be reconnected */}
          {!account.inactive && account.accountType === 'plaid' && (
            <Label
              color={account?.accounts?.needsReconnection ? 'red' : 'green'}
              horizontal
            >
              {account?.accounts?.needsReconnection
                ? 'Needs Reconnect'
                : 'Connected'}
            </Label>
          )}

          {/* Only mark for Plaid accounts; ignore manual account */}
          {noRecentPlaidTransactions && (
            <Label horizontal color="red">
              No recent transactions
            </Label>
          )}

          <SemanticCard.Header>
            <Header as="h4" style={{ paddingLeft: 0 }}>
              {account.plaidInstitutionName}
            </Header>
          </SemanticCard.Header>
          <SemanticCard.Description>
            <p>
              <b>{account.name}:</b> {account.mask}
            </p>
            <p>
              {account.type}&nbsp;—&nbsp;{account.subtype}
            </p>
          </SemanticCard.Description>
        </SemanticCard.Content>

        {isAdmin && account.manualAccount && (
          <SemanticCard.Content>
            <b>Manual Account Linked: </b> <Icon icon={faCheck} color="green" />
            <br />
            <b>Name: </b>
            {account.manualAccount.name}
          </SemanticCard.Content>
        )}

        {account?.accounts?.needsReconnection && (
          <SemanticCard.Content>
            <p>
              <b>Error: </b>
            </p>
            <p>Account needs reconnecting</p>
            <p>
              <b>Disconnected On: </b>
            </p>
            <p>
              {DateTime.fromISO(
                String(account.accounts.disconnectedOn)
              ).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}
            </p>
          </SemanticCard.Content>
        )}

        {noRecentPlaidTransactions && (
          <SemanticCard.Content>
            <p>
              <b>Error: </b>
            </p>
            <p>Over 7 days since last transaction</p>
            <p>
              <b>Date of Last Transaction On: </b>
            </p>
            <p>{dateOfLastTransaction}</p>
          </SemanticCard.Content>
        )}

        {account?.accounts?.errorObject && (
          <SemanticCard.Content>
            <p>
              <b>Error Type:</b> {account?.accounts?.errorObject.error_code}
            </p>
            <p>
              <b>Error Message:</b>{' '}
              {account?.accounts?.errorObject.error_message}
            </p>
          </SemanticCard.Content>
        )}

        <SemanticCard.Content>
          {account.inactive && (
            <SemanticButton basic fluid size="tiny" disabled>
              Inactive Account
            </SemanticButton>
          )}
          {!account.inactive && (
            <SemanticButton
              basic
              fluid
              size="tiny"
              onClick={handleMarkInactive}
            >
              Mark Inactive
            </SemanticButton>
          )}
          <MarkInactiveDialog
            open={inactiveModalOpen}
            account={account}
            userId={userId}
            onClose={() => setInactiveModalOpen(false)}
            onMarkInactive={markAccountInactive}
          />
          <SemanticButton
            basic
            disabled={forcePullDisabled()}
            fluid
            size="tiny"
            onClick={() => pullTransactions()}
            loading={submitting}
          >
            Force Pull
          </SemanticButton>

          <SemanticButton
            basic
            disabled={notifyDisabled()}
            fluid
            size="tiny"
            onClick={() => notifyUser()}
            loading={notifying}
          >
            Notify Reconnect
          </SemanticButton>
          {status && <i className="red">{status}</i>}
        </SemanticCard.Content>
      </SemanticCard>
    )
  } else {
    return (
      <Card
        content={
          <>
            {/* <RemoveAccountButton account={account} isAdmin={isAdmin} /> */}
            <Text as="bodyMd">{account.plaidInstitutionName}</Text>
            <Text as="bodySm">
              {account.name}: {account.mask}
            </Text>
            <br />
            {account.accounts?.needsReconnection && (
              <PlaidLink account={account} />
            )}
          </>
        }
      />
    )
  }
}

export default AccountCard
